import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { Plural, Trans } from "@lingui/react/macro";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Heading,
  Text,
  VStack,
} from "@wootingkb/component-library";

const POLL_INTERVAL_MS = 5000;

export default function QueuePage() {
  const router = useRouter();
  const [position, setPosition] = useState<number>(0);
  const [wait, setWait] = useState<number>(0);

  // start from the values the proxy passed in so we don't flash 0
  useEffect(() => {
    const { position: pos, wait: waitTime } = router.query;

    if (pos) setPosition(parseInt(pos as string));
    if (waitTime) setWait(parseInt(waitTime as string));
  }, [router.query]);

  // poll for our spot instead of reloading, only reload once we're let in
  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const res = await fetch("/api/queue-status", {
          headers: { Accept: "application/json" },
        });
        if (!res.ok || cancelled) return;

        const data = (await res.json()) as {
          admitted: boolean;
          position: number;
          wait: number;
        };
        if (cancelled) return;

        if (data.admitted) {
          window.location.reload();
          return;
        }

        setPosition(data.position);
        setWait(data.wait);
      } catch {
        // ignore, next tick retries
      }
    }

    const interval = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <Heading size="h2" weight="bold">
          <Trans id="Queue_its_very_busy_here">It’s very busy here!</Trans>
        </Heading>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Text>
          <Trans id="Queue_due_to_capacity_limits">
            Due to capacity limits we have to place you in a waiting room before
            you can access your orders and account.
          </Trans>
        </Text>
        <VStack>
          <Text weight="semibold">
            <Trans id="Queue_your_queue_position_is">
              Your queue position is:
            </Trans>
          </Text>
          <Text
            weight="bold"
            size="lg"
            className="text-foreground-accent-primary"
          >
            {position}
          </Text>
        </VStack>
        <VStack>
          <Text weight="semibold">
            <Trans id="Queue_estimated_wait_time">Estimated wait time:</Trans>
          </Text>
          <Text
            weight="bold"
            size="lg"
            className="text-foreground-accent-primary"
          >
            <Plural
              id="Queue_estimated_wait_time_value"
              value={wait}
              one="# minute"
              other="# minutes"
            />
          </Text>
        </VStack>
      </CardContent>
      <CardFooter>
        <Text size="sm" className="text-foreground-secondary">
          <Trans id="Queue_this_page_will_automatically_refresh">
            This page will automatically refresh, please don’t close this tab.
            Closing this window might increase waiting times.
          </Trans>
        </Text>
      </CardFooter>
    </Card>
  );
}
